import { UserProfile, UserWorkoutSession } from 'fitbeat.gym-core';
import { ILiveStreamOptions } from 'fitbeat.models';
import React, { useEffect } from 'react';
import { FunctionComponent } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { connect } from 'react-redux';
import UserIcon from '../../assets/icons/user.svg';
import {
  getOpenInviteClassList,
  influencerBookingCardPressed as influencerBookingCardPressedAction,
  workoutBookingSliderCardPressed as workoutBookingSliderCardPressedAction,
} from '../../Bookings';
import { Avatar } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import { footerText } from '../../SummaryDashboard/components/livestream-card-helpers';
import Theme from '../../theme';
import { defaultScale, getOpenInviteParticipantCount } from '../../utils';
import { createBookingServiceClient } from '../../utils/apiClientFactory';
import { getSlotTime } from '../../utils/getSlotTime';
import { isWeb } from '../../utils/ScreenSizes';
import { avatarSize, getBookingTime, plannerStyles } from './planner-shared-styles';

interface IProps {
  userProfile: UserProfile;
  workoutSession: UserWorkoutSession;
  t: (key: string, params?: any) => string;
  itemIndex: number;
  livestreamOptions?: ILiveStreamOptions;
  influencerName: string;
  booked?: boolean;
}

interface IMappedState {
  participantCount?: number;
}

interface IMappedDispatch {
  influencerInviteCardPressed(): void;
  bookedInfluencerCardPressed(): void;
  fetchBookingClassList(bookingId: string): void;
}

const PlannerInfluencerBooking: FunctionComponent<IProps & IMappedDispatch & IMappedState> = (
  {
    booked,
    itemIndex,
    influencerInviteCardPressed,
    bookedInfluencerCardPressed,
    fetchBookingClassList,
    workoutSession,
    livestreamOptions,
    influencerName,
    participantCount,
    t,
  },
) => {
  useEffect(() => {
    fetchBookingClassList(workoutSession.uniqueReference);
  }, []);

  const textColorStyle = {
    color: booked ? Theme.WHITE : Theme.GREY_DARK,
  };

  return (
    <TouchableOpacity
      onPress={() => booked ? bookedInfluencerCardPressed() : influencerInviteCardPressed()}
      style={[
        plannerStyles.plannerEntry,
        booked ? plannerStyles.confirmedEntry : plannerStyles.unConfirmedEntry,
        itemIndex ? plannerStyles.secondCardOnwards : plannerStyles.firstCard,
      ]}
    >
      <View style={styles.contentContainer}>
        <View>
          <Text style={[styles.heading, textColorStyle]}>
            {`${t('Live')} - ${getBookingTime(workoutSession)}  |  ${getSlotTime(workoutSession)}`}
          </Text>
          <Text style={[styles.bodyCopy, textColorStyle]}>{footerText(influencerName, t, livestreamOptions)}</Text>
        </View>
      </View>
      <View style={styles.avatarContainer}>
        <View>
          <Avatar
            size={avatarSize}
            customStyle={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: avatarSize / 2,
              borderWidth: 0,
            }}
            isInfluencer
            avatarId={workoutSession.pairedSessionUserProfileId || workoutSession.userProfileId}
            showIcon={false}
          />
        </View>
        <View style={[styles.participantCount, !participantCount && styles.hidden]}>
          <UserIcon color={textColorStyle.color} />
          <Text style={[styles.participantCountText, textColorStyle]}>
            {t('bookings:joining', { count: participantCount })}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const mapStateToProps = (state: IAppStore, ownProps: IProps): IMappedState => {
  const { openInviteClassesByWorkoutId } = state.bookings.bookings;
  const openInviteClass = openInviteClassesByWorkoutId[ownProps.workoutSession.uniqueReference];
  return {
    participantCount: getOpenInviteParticipantCount(openInviteClass),
  };
};

const mapDispatchToProps = (dispatch, ownProps: IProps): IMappedDispatch => {
  const { userProfile, workoutSession } = ownProps;
  const openBookingsAfterForFixOnPlannerFromWeb = isWeb();
  return {
    influencerInviteCardPressed: () => dispatch(
      influencerBookingCardPressedAction(userProfile, workoutSession, openBookingsAfterForFixOnPlannerFromWeb),
    ),
    bookedInfluencerCardPressed: () => dispatch(workoutBookingSliderCardPressedAction(userProfile, workoutSession)),
    fetchBookingClassList: (workoutId) => dispatch(
      getOpenInviteClassList(createBookingServiceClient(), workoutId, true)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PlannerInfluencerBooking);

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  heading: {
    ...Theme.textStyle.bodyCopyBold,
  },
  bodyCopy: {
    ...Theme.textStyle.bodyCopyReg,
    marginTop: defaultScale(5),
  },
  participantCount: {
    marginTop: defaultScale(5),
    flexDirection: 'row',
    alignItems: 'center',
  },
  participantCountText: {
    ...Theme.textStyle.small,
    marginLeft: defaultScale(3),
  },
  hidden: {
    opacity: 0,
  },
  avatarContainer: {
    alignItems: 'flex-end',
  },
});
